/**
 * Device-local persistent settings — localStorage-backed signals that are
 * additionally mirrored to a Tauri store file when running under Tauri.
 *
 * Why: on Android the system webview commits localStorage to disk lazily, and
 * the Tauri activity is typically killed without a clean webview shutdown, so
 * localStorage writes get lost across app restarts (IndexedDB commits at
 * write time and survives fine). There is no tauri.conf.json/wry knob for
 * this, so under Tauri the store file in the app data dir is the source of
 * truth: every set is written through to it, and on startup the signal is
 * re-hydrated from it (localStorage still serves as the synchronous initial
 * value where it does survive, e.g. desktop).
 *
 * These values are device-local by design (API keys!) — they must NEVER be
 * written into applog threads, which are unencrypted and sync.
 */

import type { Signal } from 'solid-js'
import type { Store } from '@tauri-apps/plugin-store'

import { Logger } from 'besonders-logger'
import { useLocalStorage } from './utils-data'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.WARN, { prefix: '[device-settings]' }) // eslint-disable-line unused-imports/no-unused-vars

// Tauri v2 always injects `window.isTauri` (independent of withGlobalTauri)
const isTauri = typeof window !== 'undefined' && !!(window as any).isTauri

let storePromise: Promise<Store> | null = null
function getStore(): Promise<Store> {
	// dynamic import so the plugin guest-js only loads (and IPC only happens) under Tauri
	storePromise ??= import('@tauri-apps/plugin-store').then(({ load }) => load('device-settings.json'))
	return storePromise
}

export function useDeviceSetting(key: string, defaultValue: string): Signal<string> {
	const [value, setValueLocal] = useLocalStorage<string>(key, defaultValue)
	if (!isTauri) return [value, setValueLocal]

	let touched = false // user wrote before hydration finished → their value wins
	getStore()
		.then(store => store.get<string>(key))
		.then((stored) => {
			if (touched || stored === undefined || stored === value()) return
			DEBUG(`hydrating ${key} from tauri store`)
			setValueLocal(() => stored)
		})
		.catch(err => WARN(`failed to hydrate ${key} from tauri store`, err))

	const setValue = ((arg) => {
		touched = true
		const v = setValueLocal(arg)
		getStore()
			.then(async (store) => {
				await store.set(key, v)
				await store.save()
			})
			.catch(err => WARN(`failed to persist ${key} to tauri store`, err))
		return v
	}) as typeof setValueLocal

	return [value, setValue]
}
